import React, { useContext } from 'react'
import { ComponentConfig, renderContext } from '@puckeditor/core'
import { ColorField } from 'breaktime-theme/src/puck/fields/ColorField'
import {
  TypographyProps,
  fontFamilyField,
  fontFamilyFallbackField,
  textTransformField,
  fontWeightOptions,
  applyTypography,
  FontWeight,
} from 'breaktime-theme/src/puck/fields/StyleFields'
import PlusCircle from 'images/inline/plus-circle.svg'
import ArrowDownCircle from 'images/inline/arrow-down-circle.svg'
import { AnswerDropdownButton } from 'steps/poll/components/Question'
import { ProductDropdownButton } from 'steps/poll/components/PollProducts'
import PollAnalyticsContext from 'steps/poll/components/PollAnalyticsContext'
import './PollDropdownButton.scss'

export type PollDropdownButtonProps = {
  label?: string
  links?: { label: string; url: string }[]
  bgColor?: string
  textColor?: string
  bgHoverColor?: string
  fontFamily?: TypographyProps['fontFamily']
  fontFamilyFallback?: TypographyProps['fontFamilyFallback']
  textTransform?: TypographyProps['textTransform']
  fontSize?: string
  fontWeight?: FontWeight
  _productContext?: boolean
}

export const POLL_DROPDOWN_BUTTON_DEFAULT_PROPS: PollDropdownButtonProps = {
  label: 'Shop Now',
  links: [],
  bgColor: '#4C3371',
  textColor: '#FFF',
  bgHoverColor: '#4C3371',
}

function PollDropdownButtonRender({
  label,
  links,
  bgColor,
  textColor,
  fontFamily,
  fontFamilyFallback,
  textTransform,
  fontSize,
  fontWeight,
  _productContext,
}: PollDropdownButtonProps) {
  const puckCtx = useContext(renderContext)
  const { onAnswerLinkClick, onAnswerDropdownClick } = useContext(PollAnalyticsContext)
  const isRenderMode = puckCtx.data.content.length > 0

  const labelStyle: React.CSSProperties = {
    ...applyTypography({ fontFamily, fontFamilyFallback, textTransform }),
    ...(fontSize ? { fontSize } : {}),
    ...(fontWeight ? { fontWeight: Number(fontWeight) || undefined } : {}),
  }

  if (isRenderMode) {
    const mappedLinks = (links ?? []).map(l => ({ ...l, link: l.url }))
    const Renderer = _productContext ? ProductDropdownButton : AnswerDropdownButton
    return (
      <Renderer
        label={label}
        links={mappedLinks}
        color={bgColor}
        labelStyle={labelStyle}
        onLinkClick={(link: string) => onAnswerLinkClick?.({ link })}
        onDropdownOpen={() => onAnswerDropdownClick?.({ dropdownTitle: label })}
        onDropdownLinkClick={(link: string) => onAnswerLinkClick?.({ link })}
      />
    )
  }

  return (
    <div
      className="content-item dropdown-button puck-dropdown-button-preview"
      style={
        {
          '--puck-dropdown-bg': bgColor ?? '#4C3371',
          '--puck-dropdown-color': textColor ?? '#FFF',
        } as React.CSSProperties
      }
    >
      <button className="puck-dropdown-btn" style={labelStyle}>
        {label || 'Shop Now'}
        {(links?.length ?? 0) === 1 && <PlusCircle />}
        {(links?.length ?? 0) > 1 && <ArrowDownCircle />}
      </button>
    </div>
  )
}

export const PuckPollDropdownButton: ComponentConfig<PollDropdownButtonProps> =
  {
    label: 'Dropdown Button',
    defaultProps: POLL_DROPDOWN_BUTTON_DEFAULT_PROPS,
    permissions: {
      drag: false,
      duplicate: false,
    },
    fields: {
      label: {
        type: 'text',
        label: 'Label',
      },
      links: {
        type: 'array',
        label: 'Links',
        arrayFields: {
          label: { type: 'text', label: 'Link Label' },
          url: { type: 'text', label: 'Link URL' },
        },
      },
      bgColor: {
        type: 'custom',
        label: 'Button Background Color',
        render: (props: any) => <ColorField {...props} />,
      },
      textColor: {
        type: 'custom',
        label: 'Button Text Color',
        render: (props: any) => <ColorField {...props} />,
      },
      bgHoverColor: {
        type: 'custom',
        label: 'Button Background Hover Color',
        render: (props: any) => <ColorField {...props} />,
      },
      fontFamily: fontFamilyField,
      fontFamilyFallback: fontFamilyFallbackField,
      textTransform: textTransformField,
      fontSize: { type: 'text', label: 'Font Size', placeholder: '14px' },
      fontWeight: { type: 'select', label: 'Font Weight', options: fontWeightOptions },
    },
    render: PollDropdownButtonRender,
  }
